import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import Navbar from './components/HomeScreen/Navbar';
import Footer from './components/HomeScreen/bottomsection/Footer';
import MetaTag from './layout/MetaTag';
import { useLocation } from './context/LocationContext';
import ShopCard from './components/marketplace/ShopCard';
import MarketplaceProductCard from './components/marketplace/MarketplaceProductCard';
import CategoryDiscoverySection from './components/marketplace/CategoryDiscoverySection';
import RecentlyViewedProducts from './components/recentlyViewed/RecentlyViewedProducts';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PublicIcon from '@mui/icons-material/Public';

import './css/home.css';

const RADIUS_STEPS = [1, 2, 3, 5, 8, 10, 15, 20, 25, 30, 40, 50, 75, 100];
const FALLBACK_TAGS = ['Fashion', 'Footwear', 'Mobile & Tech', 'Beauty'];

const RADIUS_QUICK_CHIPS = [
    { label: '1 km', value: 1 },
    { label: '3 km', value: 3 },
    { label: '5 km', value: 5 },
    { label: '10 km', value: 10 },
    { label: '25 km', value: 25 },
    { label: '50 km', value: 50 },
    { label: 'All India', value: 'all' }
];


const getScopeInfo = (km, isAll) => {
    if (isAll) return { icon: '🇮🇳', label: 'Nationwide Reach', badge: 'All India' };
    if (km <= 3) return { icon: '🎯', label: 'Neighborhood radius', badge: 'Hyperlocal' };
    if (km <= 10) return { icon: '🏘️', label: 'Local Town radius', badge: 'Town Reach' };
    if (km <= 25) return { icon: '🏙️', label: 'City-wide radius', badge: 'City Reach' };
    if (km <= 50) return { icon: '🚗', label: 'Metro District', badge: 'Metro' };
    return { icon: '🗺️', label: 'Regional radius', badge: 'Regional' };
};

const VALUE_PILLARS = [
    {
        icon: <StorefrontIcon />,
        title: 'Discover local shops',
        body: 'Find the brick-and-mortar stores, boutiques and merchants actually operating in your neighbourhood.'
    },
    {
        icon: <CompareArrowsOutlinedIcon />,
        title: 'Compare nearby prices',
        body: 'Check what several local shops charge before you commit to one of them.'
    },
    {
        icon: <PhoneInTalkOutlinedIcon />,
        title: 'Talk to the shopkeeper',
        body: 'Call or WhatsApp about sizes, colours and in-store discounts before you travel.'
    },
    {
        icon: <ShoppingBagOutlinedIcon />,
        title: 'Buy your way',
        body: 'Order online for home delivery, or walk in and try things on in person.'
    }
];

/* Matches the real card silhouette so the loading → ready swap doesn't shift layout. */
function CardSkeleton({ variant }) {
    return (
        <div className={`im-skel-card${variant === 'product' ? ' im-skel-card--product' : ''}`}>
            <div className="im-skel-card__media im-skeleton" />
            <div className="im-skel-card__body">
                <div className="im-skeleton im-skel-line im-skel-line--title" />
                <div className="im-skeleton im-skel-line im-skel-line--short" />
                <div className="im-skeleton im-skel-line" />
                <div className="im-skeleton im-skel-line im-skel-line--btn" />
            </div>
        </div>
    );
}

function SkeletonGrid({ count, variant, label }) {
    return (
        <div
            className={`im-grid im-grid--${variant === 'product' ? 'products' : 'shops'}`}
            role="status"
            aria-busy="true"
        >
            <span className="im-sr-only">{label}</span>
            {Array.from({ length: count }, (unused, i) => (
                <CardSkeleton key={i} variant={variant} />
            ))}
        </div>
    );
}

function ErrorState({ onRetry }) {
    return (
        <div className="im-state im-state--error" role="alert">
            <ErrorOutlineIcon className="im-state__icon" style={{ fontSize: '40px' }} />
            <h3>We couldn&apos;t load shops near you</h3>
            <p>
                The marketplace didn&apos;t respond. This is usually temporary — your connection or
                our server. Nothing is wrong with your location.
            </p>
            <button type="button" className="im-btn im-btn--primary" onClick={onRetry}>
                Try again
            </button>
        </div>
    );
}

function Home() {
    const history = useHistory();
    const { location, setIsLocationModalOpen, setRadius, isDetecting, gpsStatus } = useLocation();

    const { lat, lng } = location;
    const radius = location.radius || 'auto';
    const isManualRadius = Boolean(location.isManualRadius && location.radius !== 'auto');

    const [searchQuery, setSearchQuery] = useState('');

    /* null means "never loaded", which is not the same as "loaded and empty".
       Conflating the two is what made the old page render "No stores found"
       on every single visit before the fetch resolved. */
    const [shops, setShops] = useState(null);
    const [products, setProducts] = useState(null);
    const [newShops, setNewShops] = useState(null);
    const [categories, setCategories] = useState(null);

    // 'loading' (first paint) | 'refreshing' (radius changed) | 'ready' | 'error'
    const [status, setStatus] = useState('loading');
    const [reloadToken, setReloadToken] = useState(0);
    const [meta, setMeta] = useState({
        totalShops: 0,
        totalProducts: 0,
        shopRadius: null,
        productRadius: null,
        shopsExpanded: false,
        productsExpanded: false,
        shopsTightened: false,
        productsTightened: false,
        isManual: false
    });

    const homeSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": "https://imonline.shop/#organization",
                "name": "IM Online Marketplace",
                "url": "https://imonline.shop/",
                "logo": "https://imonline.shop/output-onlinepngtools.png"
            },
            {
                "@type": "WebSite",
                "@id": "https://imonline.shop/#website",
                "url": "https://imonline.shop/",
                "name": "IM Online Marketplace",
                "description": "Discover local shops and products around you. Compare prices and choose online delivery or in-store purchase."
            }
        ]
    };

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        // First paint shows skeletons; a radius change dims the existing results
        // instead of collapsing the page and regrowing it.
        setStatus((prev) => (prev === 'ready' ? 'refreshing' : 'loading'));

        const queryParts = [];
        if (lat !== null && lat !== undefined && !isNaN(lat)) queryParts.push(`lat=${lat}`);
        if (lng !== null && lng !== undefined && !isNaN(lng)) queryParts.push(`lng=${lng}`);
        if (location.city && location.city !== 'All Locations') queryParts.push(`city=${encodeURIComponent(location.city)}`);
        if (location.pincode) queryParts.push(`pincode=${encodeURIComponent(location.pincode)}`);
        const geo = queryParts.join('&');
        const geoPrefix = geo ? `${geo}&` : '';
        const manualParam = isManualRadius && radius !== 'all' ? '&manual=true' : '';
        // Per-request catch: one dead endpoint shouldn't blank all four sections.
        const get = (url) =>
            axios.get(url, { signal }).then((r) => r.data).catch(() => null);

        Promise.all([
            get(`/api/marketplace/shops?${geoPrefix}radius=${radius}${manualParam}&limit=6`),
            get(`/api/marketplace/products?${geoPrefix}radius=${radius}${manualParam}&limit=8`),
            get(`/api/marketplace/new-shops?${geo}`),
            get(`/api/marketplace/categories?${geo}`)
        ]).then(([shopsRes, prodsRes, newRes, catRes]) => {
            if (signal.aborted) return;

            // Shops are what this page is built around — if that call failed, the
            // page failed. A missing categories response is survivable.
            if (!shopsRes) {
                setStatus('error');
                return;
            }

            setShops(shopsRes.shops || []);
            setProducts(prodsRes ? prodsRes.products || [] : []);
            setNewShops(newRes ? newRes.newShops || [] : []);
            setCategories(catRes ? catRes.categories || [] : []);
            setMeta({
                totalShops: shopsRes.totalShops || 0,
                totalProducts: prodsRes ? prodsRes.totalProducts || 0 : 0,
                // Shops and products widen their radius independently, so each
                // section reports its own.
                shopRadius: shopsRes.activeRadius,
                productRadius: prodsRes ? prodsRes.activeRadius : null,
                shopsExpanded: !!shopsRes.autoExpanded,
                productsExpanded: !!(prodsRes && prodsRes.autoExpanded),
                shopsTightened: !!shopsRes.autoTightened,
                productsTightened: !!(prodsRes && prodsRes.autoTightened),
                isManual: !!shopsRes.isManual
            });
            setStatus('ready');
        });

        return () => controller.abort();
    }, [lat, lng, location.city, location.pincode, radius, isManualRadius, reloadToken]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        history.push(`/marketplace/search?q=${encodeURIComponent(searchQuery.trim())}`);
    };

    const isFirstLoad = status === 'loading';
    const isRefreshing = status === 'refreshing';
    const hasFailed = status === 'error';
    const dimWhileRefreshing = isRefreshing ? 'im-is-refreshing' : '';

    const describeRadius = (value) => {
        if (value === 'all' || value === 9999) return 'All India';
        if (!value || value === 'auto') return '5 km';
        return `${value} km`;
    };

    const getEffectiveKm = () => {
        if (radius === 'all') return 100;
        if (radius === 'auto' || !isManualRadius) {
            return typeof meta.shopRadius === 'number' && meta.shopRadius > 0 ? meta.shopRadius : 5;
        }
        const n = Number(radius);
        return isNaN(n) || n <= 0 ? 5 : n;
    };

    const handleDecreaseRadius = () => {
        if (radius === 'all') {
            setRadius(100, true);
            return;
        }
        const currentKm = getEffectiveKm();
        const prevStep = [...RADIUS_STEPS].reverse().find((s) => s < currentKm);
        if (prevStep !== undefined) {
            setRadius(prevStep, true);
        } else if (currentKm > 1) {
            setRadius(1, true);
        }
    };

    const handleIncreaseRadius = () => {
        if (radius === 'all') return;
        const currentKm = getEffectiveKm();
        const nextStep = RADIUS_STEPS.find((s) => s > currentKm);
        if (nextStep !== undefined) {
            setRadius(nextStep, true);
        } else {
            setRadius('all', true);
        }
    };

    const handleSliderChange = (e) => {
        const val = Number(e.target.value);
        if (!isNaN(val) && val >= 1) {
            setRadius(val, true);
        }
    };

    // Tags come from the live category list rather than the old hardcoded
    // footwear terms, which no longer match a multi-category marketplace.
    const tags = (categories || []).slice(0, 6).map((c) => c.shortName || c.name);
    const displayTags = tags.length > 0 ? tags : FALLBACK_TAGS;

    const currentKm = getEffectiveKm();
    const isAllIndia = radius === 'all';
    const sliderVal = isAllIndia ? 100 : currentKm;
    const sliderPercent = isAllIndia ? 100 : Math.min(100, Math.max(0, ((currentKm - 1) / 99) * 100));
    const scope = getScopeInfo(currentKm, isAllIndia);

    const renderExpandedNotice = (expanded, activeRadius) => {
        if (!expanded || activeRadius === null || activeRadius === undefined || activeRadius === 'all') return null;
        return (
            <div className="im-notice im-notice--warn" role="status">
                <InfoOutlinedIcon style={{ fontSize: '18px' }} />
                <span>
                    Limited vendors in 5 km, so search area was dynamically expanded to{' '}
                    <strong>{describeRadius(activeRadius)}</strong> to find nearest local options.
                </span>
            </div>
        );
    };

    return (
        <main className="im-home">
            <MetaTag
                title="IM Online | Discover Local Shops & Products Near You"
                description="Shop local with IM Online. Find neighborhood stores, compare nearby prices, contact sellers, and choose online delivery or in-store pickup."
                keywords="local shops, buy near me, local marketplace, nearby stores, IM Online"
                canonical="https://imonline.shop/"
                schema={homeSchema}
            />

            <Navbar />

            {/* ── 1. Hero: Classic, Premium Marketplace Header ── */}
            <section className="im-hero">
                <div className="im-container">
                    <div className="im-hero__inner">
                        {/* Top Location Bar */}
                        <div className="im-hero__top-bar">
                            <button
                                type="button"
                                className="im-loc-pill"
                                onClick={() => setIsLocationModalOpen(true)}
                                aria-label="Current shopping location. Click to change."
                            >
                                <LocationOnIcon
                                    className="im-loc-pill__icon"
                                    style={{ fontSize: '16px', color: gpsStatus === 'granted' ? '#10B981' : '#a85050' }}
                                />
                                <span className="im-loc-pill__text">
                                    {isDetecting
                                        ? <span className="im-loc-detecting">Detecting your location…</span>
                                        : <>Shopping near <strong>{location.label}</strong></>
                                    }
                                </span>
                                {!isDetecting && (
                                    <span className="im-loc-pill__change">Change</span>
                                )}
                            </button>
                        </div>

                        {/* GPS Detection Banner */}
                        {isDetecting && (
                            <div className="im-gps-banner im-gps-banner--detecting" role="status" aria-live="polite">
                                <span className="im-gps-spinner" aria-hidden="true">📡</span>
                                <span>Finding shops near your current location…</span>
                            </div>
                        )}
                        {gpsStatus === 'granted' && !isDetecting && (
                            <div className="im-gps-banner im-gps-banner--granted" role="status" aria-live="polite">
                                <span aria-hidden="true">📍</span>
                                <span>Showing shops near <strong>{location.area}</strong></span>
                            </div>
                        )}
                        {gpsStatus === 'denied' && !isDetecting && (
                            <div className="im-gps-banner im-gps-banner--denied" role="status" aria-live="polite">
                                <span aria-hidden="true">ℹ️</span>
                                <span>
                                    Location access denied. 
                                    <button
                                        type="button"
                                        className="im-link-more"
                                        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 600 }}
                                        onClick={() => setIsLocationModalOpen(true)}
                                    >
                                        Choose a city manually
                                    </button>
                                </span>
                            </div>
                        )}

                        {/* Heading */}
                        <h1 className="im-hero__heading">Shop local. Discover more.</h1>

                        <p className="im-hero__lede">
                            Find nearby stores, compare products and prices, then choose whether to
                            order online or walk in and buy it today.
                        </p>

                        {/* Search Bar */}
                        <form className="im-hero__search" onSubmit={handleSearchSubmit}>
                            <label className="im-sr-only" htmlFor="im-home-search">
                                Search for products or shops near you
                            </label>
                            <div className="im-search">
                                <SearchIcon className="im-search__icon" />
                                <input
                                    id="im-home-search"
                                    type="text"
                                    placeholder="Search sneakers, electronics, fashion, groceries…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    autoComplete="off"
                                />
                                <button type="submit" className="im-search-submit-btn" aria-label="Search near me">
                                    <span className="im-search-btn-full">Search near me</span>
                                    <span className="im-search-btn-short">Search</span>
                                </button>
                            </div>
                        </form>

                        {/* Classic, Premium Search Radius Card */}
                        <div className="im-radius-expander" role="region" aria-label="Search area radius expander">
                            <div className="im-radius-expander__header">
                                <div className="im-radius-expander__tag">
                                    <span className="im-radius-expander__pulse" />
                                    <span className="im-radius-expander__tag-text">Search Area</span>
                                </div>

                                <div className="im-radius-expander__presets" role="group" aria-label="Quick area presets">
                                    <button
                                        type="button"
                                        className={`im-radius-preset ${(!isManualRadius || radius === 'auto') ? 'im-radius-preset--active' : ''}`}
                                        onClick={() => setRadius('auto', false)}
                                        title="Automatically adapt search radius based on local shop density"
                                    >
                                        <FlashOnIcon style={{ fontSize: '14px' }} />
                                        <span>Dynamic</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`im-radius-preset ${radius === 'all' ? 'im-radius-preset--active' : ''}`}
                                        onClick={() => setRadius('all', true)}
                                        title="Expand search across all of India"
                                    >
                                        <PublicIcon style={{ fontSize: '14px' }} />
                                        <span>All India</span>
                                    </button>
                                </div>
                            </div>

                            {/* Stepper + Value Center */}
                            <div className="im-radius-stepper-row">
                                <button
                                    type="button"
                                    className="im-radius-btn im-radius-btn--decrease"
                                    onClick={handleDecreaseRadius}
                                    disabled={radius !== 'all' && currentKm <= 1}
                                    aria-label="Decrease search area"
                                    title="Shrink area to closer shops"
                                >
                                    <RemoveIcon style={{ fontSize: '20px' }} />
                                </button>

                                <div className="im-radius-hub">
                                    <div className="im-radius-hub__value">
                                        {isAllIndia ? (
                                            <span className="im-radius-hub__all">All India</span>
                                        ) : (
                                            <>
                                                <span className="im-radius-hub__num">{currentKm}</span>
                                                <span className="im-radius-hub__unit">km</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="im-radius-hub__scope-badge">
                                        <span className="im-radius-hub__scope-icon">{scope.icon}</span>
                                        <span className="im-radius-hub__scope-text">
                                            {isAllIndia
                                                ? 'Nationwide reach'
                                                : (!isManualRadius || radius === 'auto')
                                                ? `Auto-tuned (${currentKm} km)`
                                                : `${scope.label}`}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="im-radius-btn im-radius-btn--increase"
                                    onClick={handleIncreaseRadius}
                                    disabled={radius === 'all'}
                                    aria-label="Increase search area"
                                    title="Expand area to discover more shops"
                                >
                                    <AddIcon style={{ fontSize: '20px' }} />
                                </button>
                            </div>

                            {/* Slider */}
                            <div className="im-radius-slider-wrap">
                                <span className="im-radius-slider-label">1 km</span>
                                <div className="im-radius-slider-track-wrap">
                                    <input
                                        type="range"
                                        min="1"
                                        max="100"
                                        step="1"
                                        value={sliderVal}
                                        onChange={handleSliderChange}
                                        className="im-radius-slider"
                                        style={{ '--slider-progress': `${sliderPercent}%` }}
                                        aria-label="Search area radius slider"
                                    />
                                </div>
                                <span className="im-radius-slider-label">100 km</span>
                            </div>

                            {/* Quick Tap Distance Chips */}
                            <div className="im-radius-quick-chips" role="group" aria-label="Quick radius select">
                                {RADIUS_QUICK_CHIPS.map((chip) => {
                                    const isCurrentActive =
                                        chip.value === 'all'
                                            ? radius === 'all'
                                            : radius !== 'all' && currentKm === chip.value;
                                    return (
                                        <button
                                            key={chip.label}
                                            type="button"
                                            className={`im-radius-chip ${isCurrentActive ? 'im-radius-chip--active' : ''}`}
                                            onClick={() => setRadius(chip.value, true)}
                                        >
                                            {chip.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Helper Hint */}
                            <div className="im-radius-hint">
                                <span>
                                    {isAllIndia
                                        ? 'Showing shops from all across India. Tap any distance chip to focus locally.'
                                        : 'Tap distance chips, drag slider, or use + / − to instantly expand your search.'}
                                </span>
                            </div>
                        </div>

                        {/* Live Proof Badge */}
                        {status === 'ready' && meta.totalShops > 0 && (
                            <div className="im-proof">
                                <span className="im-proof__item">
                                    <span className="im-proof__dot" />
                                    <strong className="im-proof__num">{meta.totalShops}</strong>{' '}
                                    {meta.totalShops === 1 ? 'store' : 'stores'}
                                </span>
                                <span className="im-proof__sep">·</span>
                                <span className="im-proof__item">
                                    <strong className="im-proof__num">{meta.totalProducts}</strong>{' '}
                                    {meta.totalProducts === 1 ? 'product' : 'products'}
                                </span>
                                <span className="im-proof__sep">·</span>
                                <span className="im-proof__item">
                                    within <strong>{describeRadius(meta.shopRadius || radius)}</strong>
                                </span>
                                {meta.shopsTightened && (
                                    <span className="im-proof__badge im-proof__badge--tight">
                                        ⚡ Auto-tightened for nearby vendors
                                    </span>
                                )}
                                {meta.shopsExpanded && (
                                    <span className="im-proof__badge im-proof__badge--expanded">
                                        ✨ Auto-expanded for more stores
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Category Tags */}
                        <div className="im-tags">
                            <span className="im-tags__label">Popular</span>
                            <div className="im-tags__list">
                                {displayTags.map((tag) => (
                                    <Link
                                        key={tag}
                                        to={`/marketplace/search?q=${encodeURIComponent(tag)}`}
                                        className="im-tag"
                                    >
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 2. Categories ── */}
            <CategoryDiscoverySection categories={categories} isLoading={isFirstLoad} />

            {/* ── 3. Shops near you ── */}
            <section className="im-shops-section">
                <div className="im-container">
                    {/* Section header */}
                    <div className="im-shops-head">
                        <div className="im-shops-head__left">
                            <div className="im-shops-head__pins">
                                <span className="im-shops-head__pin" />
                                <span className="im-shops-head__pin" />
                                <span className="im-shops-head__pin" />
                            </div>
                            <h2>
                                Shops near you&nbsp;
                                {status === 'ready' && meta.totalShops > 0 && (
                                    <span className="im-shops-head__count">
                                        {meta.totalShops} {meta.totalShops === 1 ? 'store' : 'stores'}
                                    </span>
                                )}
                            </h2>
                            <p className="im-shops-head__sub">
                                Real storefronts you can walk in to — sorted by proximity.
                            </p>
                        </div>
                        {meta.totalShops > 0 && (
                            <Link to="/marketplace/search?type=shops" className="im-shops-view-all">
                                View all &rarr;
                            </Link>
                        )}
                    </div>

                    {renderExpandedNotice(meta.shopsExpanded, meta.shopRadius)}

                    {/* Loading skeletons */}
                    {isFirstLoad && (
                        <SkeletonGrid count={6} variant="shop" label="Loading shops near you" />
                    )}

                    {hasFailed && <ErrorState onRetry={() => setReloadToken((t) => t + 1)} />}

                    {/* Shops grid — first shop is rendered as a "featured" spotlight card */}
                    {!isFirstLoad && !hasFailed && shops && shops.length > 0 && (
                        <div className={`im-shops-grid ${dimWhileRefreshing}`}>
                            {shops.map((shop, idx) => (
                                <ShopCard
                                    key={shop._id}
                                    shop={shop}
                                    featured={idx === 0}
                                />
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!isFirstLoad && !hasFailed && shops && shops.length === 0 && (
                        <div className="im-state">
                            <StorefrontIcon className="im-state__icon" style={{ fontSize: '40px' }} />
                            <h3>No shops found within {describeRadius(meta.shopRadius || radius)}</h3>
                            <p>
                                We don&apos;t have registered shops in your immediate neighbourhood yet.
                                {radius !== 'all' ? ' You can expand the radius or explore all verified stores across India.' : ''}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '16px' }}>
                                {radius !== 'all' && (
                                    <button
                                        type="button"
                                        className="im-btn im-btn--primary"
                                        onClick={() => setRadius('all', true)}
                                    >
                                        Browse All Stores Across India
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="im-btn im-btn--ghost"
                                    onClick={() => setIsLocationModalOpen(true)}
                                >
                                    Change Location
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ── 4. Products near you ── */}
            <section className="im-section">
                <div className="im-container">
                    <div className="im-section__head">
                        <div>
                            <span className="im-eyebrow">Nearby catalogue</span>
                            <h2>Products available near you</h2>
                            <p className="im-section__sub">
                                In stock at a local shop right now — delivered, or collected in person.
                            </p>
                        </div>
                        {meta.totalProducts > 0 && (
                            <Link to="/marketplace/search?type=products" className="im-link-more">
                                View all {meta.totalProducts} &rarr;
                            </Link>
                        )}
                    </div>

                    {renderExpandedNotice(meta.productsExpanded, meta.productRadius)}

                    {isFirstLoad && (
                        <SkeletonGrid
                            count={4}
                            variant="product"
                            label="Loading products near you"
                        />
                    )}

                    {!isFirstLoad && products && products.length > 0 && (
                        <div className={`im-grid im-grid--products ${dimWhileRefreshing}`}>
                            {products.map((prod) => (
                                <MarketplaceProductCard key={prod._id} product={prod} />
                            ))}
                        </div>
                    )}

                    {!isFirstLoad && !hasFailed && products && products.length === 0 && (
                        <div className="im-state">
                            <Inventory2OutlinedIcon
                                className="im-state__icon"
                                style={{ fontSize: '40px' }}
                            />
                            <h3>No products listed within {describeRadius(meta.productRadius || radius)}</h3>
                            <p>
                                Shops in this area haven&apos;t published their catalogue yet. You can
                                browse verified shops directly or explore all products nationwide.
                            </p>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '16px' }}>
                                {radius !== 'all' && (
                                    <button
                                        type="button"
                                        className="im-btn im-btn--primary"
                                        onClick={() => setRadius('all', true)}
                                    >
                                        Browse Products Across India
                                    </button>
                                )}
                                <Link to="/marketplace/search?type=shops" className="im-btn im-btn--ghost">
                                    Browse shops instead
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ── 5. New shops — a rail, so it can't be mistaken for §3.
                   Deliberately not called "near you": /new-shops returns the newest
                   stores platform-wide and ignores the radius entirely. ── */}
            {newShops && newShops.length > 0 && (
                <section className="im-section im-section--raised">
                    <div className="im-container">
                        <div className="im-section__head">
                            <div>
                                <span className="im-eyebrow">Recently joined</span>
                                <h2>New on IM Online</h2>
                                <p className="im-section__sub">
                                    The latest shops to open a storefront — city-wide, not filtered by
                                    your radius.
                                </p>
                            </div>
                        </div>

                        <div className="im-rail">
                            {newShops.map((shop) => (
                                <ShopCard
                                    key={shop._id}
                                    shop={shop}
                                    compact
                                    joinedText={shop.joinedText}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── 6. Recently viewed. Re-engagement sits with the content sections,
                not after the closing CTA — so the brand gradient below runs
                straight into the dark footer instead of being interrupted by a
                pale band. ── */}
            <RecentlyViewedProducts title="Recently Viewed Products" />

            {/* ── 7. Why local + closing CTA (merges the two duplicate value blocks) ── */}
            <section className="im-closing">
                <div className="im-container">
                    <div className="im-closing__head">
                        <span className="im-eyebrow">The local marketplace mission</span>
                        <h2>Why shop with IM Online?</h2>
                        <p>
                            We put your neighbourhood businesses online without taking away the part
                            that makes them good — the shop, and the person in it.
                        </p>
                    </div>

                    <div className="im-values">
                        {VALUE_PILLARS.map((pillar) => (
                            <div className="im-value" key={pillar.title}>
                                <div className="im-value__icon">{pillar.icon}</div>
                                <div>
                                    <strong>{pillar.title}</strong>
                                    <span>{pillar.body}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="im-closing__cta">
                        <Link
                            to="/marketplace/search?type=shops"
                            className="im-btn im-btn--onbrand"
                        >
                            Explore nearby stores &rarr;
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

export default Home;
